/**
 * RelatedPaymentsTable - Standardized table for displaying payments
 * 
 * Used on:
 * - Bill detail page
 * - Order detail page
 * - Vendor detail page
 * 
 * Shows: Date, Amount, Method, Reference, Status, Actions
 */

import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, 
  Eye, 
  Calendar, 
  CreditCard,
  CheckCircle,
  Clock,
  Ban,
} from 'lucide-react';

// Payment status configuration
const PAYMENT_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  applied: { label: 'Applied', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  voided: { label: 'Voided', color: 'bg-gray-100 text-gray-500', icon: Ban },
  void: { label: 'Void', color: 'bg-gray-100 text-gray-500', icon: Ban },
};

// Payment method labels
const PAYMENT_METHOD_LABELS = {
  check: 'Check',
  ach: 'ACH',
  credit_card: 'Credit Card',
  wire: 'Wire',
  cash: 'Cash',
  other: 'Other',
};

// Format currency (from cents)
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const RelatedPaymentsTable = ({ 
  payments = [], 
  onViewPayment,
  emptyMessage = 'No payments recorded',
  showAppliedAmount = false, // Show applied amount instead of total amount
}) => {
  if (!payments || payments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="related-payments-table">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Date</th>
            <th className="px-3 py-2 text-left font-medium">Payment #</th>
            <th className="px-3 py-2 text-right font-medium">
              {showAppliedAmount ? 'Applied' : 'Amount'}
            </th>
            <th className="px-3 py-2 text-left font-medium">Method</th>
            <th className="px-3 py-2 text-left font-medium">Reference</th>
            <th className="px-3 py-2 text-left font-medium">Status</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {payments.map(payment => {
            const statusConfig = PAYMENT_STATUS_CONFIG[payment.status] || PAYMENT_STATUS_CONFIG.completed;
            const StatusIcon = statusConfig.icon;
            const displayAmount = showAppliedAmount 
              ? (payment.applied_amount_cents || payment.amount_cents)
              : payment.amount_cents;
            
            return (
              <tr 
                key={payment.id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onViewPayment?.(payment.id)}
                data-testid={`payment-row-${payment.id}`}
              >
                <td className="px-3 py-2 text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(payment.payment_date)}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-sm">
                  {payment.payment_number || payment.id?.slice(0, 10)}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">
                  {formatCurrency(displayAmount)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <CreditCard className="w-3 h-3 text-gray-400" />
                    {PAYMENT_METHOD_LABELS[payment.payment_method] || payment.payment_method}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-gray-500">
                  {payment.reference_number || '-'}
                </td>
                <td className="px-3 py-2">
                  <Badge className={`${statusConfig.color} text-xs`}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewPayment?.(payment.id);
                    }}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
        
        {/* Totals footer */}
        {payments.length > 0 && (
          <tfoot className="bg-gray-50 font-medium">
            <tr>
              <td colSpan={2} className="px-3 py-2 text-right">Total:</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatCurrency(
                  payments.reduce((sum, p) => {
                    const amt = showAppliedAmount 
                      ? (p.applied_amount_cents || p.amount_cents)
                      : p.amount_cents;
                    return sum + (amt || 0);
                  }, 0)
                )}
              </td>
              <td colSpan={4}></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default RelatedPaymentsTable;
